import { navigate } from '@reach/router';
import { useState } from 'react';
import authServiceInstaller from '../../lib/authServiceInstaller';
import routes from '../../routes';
import useFormState, { convertValidationRules } from '../useFormState';
import { BaseValidationContext } from '../useFormState.types';
import useWrapWithLoader from '../useWrapWithLoading';
import { validateEmail, isValidPassword } from '../../util/form-validation';
import useUtil from './useUtil';

type RequestModel = {
  email: string;
};

type ResetModel = {
  code: string;
  password: string;
};

export const createEmailValidateFunction = () =>
  convertValidationRules<RequestModel>(
    (_context: BaseValidationContext<RequestModel>) => {
      return {
        email: validateEmail,
      };
    },
  );

export const createResetValidateFunction = () =>
  convertValidationRules<ResetModel>(
    (_context: BaseValidationContext<ResetModel>) => {
      return {
        code: (value: string) => (!!value ? null : 'Code is required'),
        password: (value: string) =>
          isValidPassword(value) ? null : 'Password is required',
      };
    },
  );

export const defaultEmailFormValues: RequestModel = {
  email: '',
};

export const defaultResetFormValues: ResetModel = {
  code: '',
  password: '',
};

const useInstallerForgotPassword = (opts: {
  defaultEmail?: string;
  genericErrorMessage: string;
  successMessage: string;
}) => {
  const { defaultEmail, genericErrorMessage, successMessage } = opts;
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const [isResetStep, setIsResetStep] = useState(false);

  const emailForm = useFormState(
    { ...defaultEmailFormValues, email: defaultEmail || '' },
    {
      validate: createEmailValidateFunction(),
    },
  );

  const resetForm = useFormState(
    { ...defaultResetFormValues },
    {
      validate: createResetValidateFunction(),
    },
  );

  const handleSendCode = emailForm.submit(
    wrapWithLoader(async () => {
      try {
        await authServiceInstaller.forgotPassword(
          emailForm.values.email.toLowerCase(),
        );
        setErrorNotification(true, successMessage, 'success');
        setIsResetStep(true);
      } catch (_error) {
        emailForm.setError('email', opts.genericErrorMessage);
      }
    }),
  );

  const handleReset = resetForm.submit(
    wrapWithLoader(async () => {
      try {
        await authServiceInstaller.verifyAndResetPassword(
          emailForm.values.email.toLowerCase(),
          resetForm.values.code,
          resetForm.values.password,
        );
        await authServiceInstaller.signInWithPassword(
          emailForm.values.email.toLowerCase(),
          resetForm.values.password,
        );
        return navigate(routes.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING, {
          replace: true,
        });
      } catch (_error) {
        setErrorNotification(true, genericErrorMessage);
      }
    }),
  );

  return {
    emailForm,
    resetForm,
    isResetStep,
    handleSendCode,
    handleReset,
  };
};

export default useInstallerForgotPassword;
